import { Button, Checkbox, FormControl, Link, Modal, Select } from '@farol-ds/react'
import { COMPONENT_NAME } from '@jusbrasil-web/shared-ga4-events'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useMemo, useState } from 'react'

import { COMPONENT_RENDER_EVENT_PROPS, GA4EventHandlers } from '../../../hooks'
import { ENROLLMENT_PERIOD_OPTIONS, TERMS_URL } from '../constants'
import { useStudentEnrollmentValidationContext } from '../context'
import { Steps } from '../enums'
import { InstitutionAutocomplete } from '../institution-autocomplete'
import styles from './steps.module.scss'

interface AcademicDataProps {
  gA4EventHandlers?: GA4EventHandlers
}

export function AcademicData(props: AcademicDataProps) {
  const { gA4EventHandlers } = props
  const { sharedData, setSharedData, setCurrentStep, eventData, viewer, texts } =
    useStudentEnrollmentValidationContext()

  const [institution, setInstitution] = useState(sharedData.institution ?? '')
  const [isInstitutionSelected, setIsInstitutionSelected] = useState(
    Boolean(sharedData.institution)
  )
  const [enrollmentPeriod, setEnrollmentPeriod] = useState(sharedData.enrollmentPeriod ?? '')
  const [termsAccepted, setTermsAccepted] = useState(sharedData.termsAccepted ?? false)

  const firstName = viewer?.name?.split(' ')[0] ?? 'Estudante'
  const title = texts?.academicData?.title ?? `${firstName}, informe os dados do seu curso`
  const description =
    texts?.academicData?.description ??
    'Preencha os dados abaixo para continuar com a validação do benefício.'
  const submitLabel = texts?.academicData?.submitButtonText ?? 'Avançar'

  const isSubmitDisabled = !isInstitutionSelected || !enrollmentPeriod || !termsAccepted
  const pid = viewer?.pid

  useEffect(() => {
    sendMetrics('UserAccount.StudentAcademicFormViewed', `${pid}`, { ...eventData })
    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.IDENTITY_VALIDATION,
      renders: COMPONENT_RENDER_EVENT_PROPS.STUDENT_ENROLLMENT_ACADEMIC_DATA,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mirror the form fields into shared data so the close tracker can report
  // filledFieldsCount even when the user dismisses without submitting.
  useEffect(() => {
    setSharedData((prev) => ({ ...prev, institution, enrollmentPeriod, termsAccepted }))
  }, [institution, enrollmentPeriod, termsAccepted, setSharedData])

  const handleSubmit = () => {
    sendMetrics('UserAccount.StudentAcademicFormSubmitted', `${pid}`, {
      ...eventData,
      metadata: {
        ...eventData?.metadata,
        institution,
        enrollmentPeriod,
      },
    })

    setSharedData({
      ...sharedData,
      institution: institution.trim(),
      enrollmentPeriod,
      termsAccepted,
    })
    setCurrentStep(Steps.UPLOAD_INSTRUCTIONS)
  }

  const termsText = useMemo(
    () =>
      texts?.academicData?.termsText ?? (
        <>
          Declaro, sob minha responsabilidade, que sou estudante regularmente matriculado(a) no
          curso de Direito e que não estou fornecendo informações falsas ou de terceiros para obter
          este benefício. Estou ciente de que, caso identificado uso em desacordo com os{' '}
          <Link
            href={TERMS_URL}
            target="_blank"
            onClick={() =>
              sendMetrics('UserAccount.StudentAcademicFormTermsClicked', `${pid}`, { ...eventData })
            }
          >
            Termos de Uso
          </Link>{' '}
          e/ou com este compromisso, o Jusbrasil poderá revogar o benefício e bloquear o acesso à
          plataforma.
        </>
      ),
    [texts?.academicData?.termsText, pid, eventData]
  )

  return (
    <>
      <Modal.Header>
        <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
        <Modal.HeaderDescription>{description}</Modal.HeaderDescription>
      </Modal.Header>
      <Modal.Body data-testid="academic-data-step">
        <div className={styles.form}>
          <InstitutionAutocomplete
            label={texts?.academicData?.institutionLabel ?? 'Instituição de ensino'}
            placeholder={
              texts?.academicData?.institutionPlaceholder ??
              'Busque pelo nome ou sigla da instituição'
            }
            value={institution}
            onInstitutionChange={setInstitution}
            onInstitutionSelected={setIsInstitutionSelected}
          />

          <FormControl required>
            <FormControl.Label>
              {texts?.academicData?.semesterLabel ?? 'Semestre do curso de Direito'}
            </FormControl.Label>
            <Select
              value={enrollmentPeriod || undefined}
              onValueChange={setEnrollmentPeriod}
              placeholder={texts?.academicData?.semesterPlaceholder ?? 'Selecionar'}
            >
              {ENROLLMENT_PERIOD_OPTIONS.map((option) => (
                <Select.Item key={option} value={option}>
                  {option}
                </Select.Item>
              ))}
            </Select>
          </FormControl>

          <Checkbox.Item
            className={styles.termsRow}
            checked={termsAccepted}
            onCheckedChange={(checked) => setTermsAccepted(checked === true)}
          >
            <Checkbox />
            <Checkbox.Content>
              <Checkbox.Name>{termsText}</Checkbox.Name>
            </Checkbox.Content>
          </Checkbox.Item>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
          {submitLabel}
        </Button>
      </Modal.Footer>
    </>
  )
}
